/**
 * UniFi Network Integration Store
 *
 * Zustand store for UniFi Network controller state.
 * Manages connection, device/client/network data, and polling controls.
 *
 * All API calls include ?orgSlug= when an org context is active, so the
 * backend scopes data to the correct organization's network.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { api, getValidatedOrganizationContextHeaderValue } from '@/services/api/client';

// ============================================================================
// Types
// ============================================================================

export interface UnifiNetworkPort {
  idx: number;
  state: string;
  connector: string;
  maxSpeedMbps: number;
  speedMbps: number;
  poe?: {
    standard: string;
    type: number;
    enabled: boolean;
    state: string;
  };
}

export interface UnifiNetworkDevice {
  id: string;
  macAddress: string;
  ipAddress: string;
  name: string;
  model: string;
  state: string;
  type: string;
  firmwareVersion: string;
  firmwareUpdatable: boolean;
  features: string[];
  ports?: UnifiNetworkPort[];
  locationName?: string;
}

export interface UnifiNetworkClient {
  id: string;
  macAddress: string;
  ipAddress: string;
  hostname: string;
  name: string;
  type: string;
  connectedAt: string;
  signal?: number;
  rssi?: number;
  channel?: number;
  essid?: string;
  apMac?: string;
  band?: string;
  switchMac?: string;
  switchPort?: number;
  txRateBps?: number;
  rxRateBps?: number;
  txBytes?: number;
  rxBytes?: number;
  networkId?: string;
  networkName?: string;
  vlan?: number;
  uptime?: number;
  uplinkDeviceId?: string;
}

export interface UnifiNetworkVlan {
  id: string;
  name: string;
  vlanId?: number;
  purpose?: string;
  ipSubnet?: string;
  dhcpEnabled?: boolean;
}

export interface UnifiSite {
  id: string;
  name: string;
}

export interface UnifiNetworkSettings {
  host: string;
  hasApiKey: boolean;
  hasWsCredentials?: boolean;
  isConnected: boolean;
  siteId?: string;
  siteName?: string;
  deviceCount?: number;
  clientCount?: number;
}

interface UnifiNetworkState {
  // Settings / connection
  hasApiKey: boolean;
  hasWsCredentials: boolean;
  wsUsername: string;
  isConnected: boolean;
  host: string;
  siteId?: string;
  siteName?: string;

  // Data
  sites: UnifiSite[];
  devices: UnifiNetworkDevice[];
  clients: UnifiNetworkClient[];
  networks: UnifiNetworkVlan[];

  // Presence
  trackedClients: Array<{ userId: string; clientMac: string; clientName: string }>;
  excludedClients: Array<{ clientMac: string; clientName: string }>;
  presence: Array<{ userId: string; clientName: string; locationName: string; isPresent: boolean; lastSeen: string }>;
  presenceLog: Array<{ eventType: string; clientName: string; locationName: string; siteName: string; timestamp: string }>;

  // UI state
  isLoading: boolean;
  isConnecting: boolean;
  error: string | null;

  // Actions
  loadSettings: () => Promise<void>;
  saveSettings: (host: string, apiKey: string, wsUsername?: string, wsPassword?: string) => Promise<void>;
  saveWsCredentials: (wsUsername: string, wsPassword: string) => Promise<void>;
  removeSettings: () => Promise<void>;
  requestDiscovery: () => Promise<void>;
  loadDevices: () => Promise<void>;
  loadClients: () => Promise<void>;
  loadNetworks: () => Promise<void>;
  restartDevice: (deviceId: string) => Promise<void>;
  powerCyclePort: (deviceId: string, portIdx: number) => Promise<void>;
  updateDeviceLocation: (deviceId: string, locationName: string) => Promise<void>;
  trackClient: (clientMac: string, userId: string, clientName: string) => Promise<void>;
  untrackClient: (clientMac: string) => Promise<void>;
  loadTrackedClients: () => Promise<void>;
  excludeClient: (clientMac: string, clientName: string) => Promise<void>;
  includeClient: (clientMac: string) => Promise<void>;
  loadExcludedClients: () => Promise<void>;
  loadPresence: () => Promise<void>;
  loadPresenceLog: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const API_BASE = '/integrations/unifi-network';

interface UnifiSettingsUpdateResponse {
  isConnected?: boolean;
}

type TrackedClient = UnifiNetworkState['trackedClients'][number];
type ExcludedClient = UnifiNetworkState['excludedClients'][number];
type PresenceEntry = UnifiNetworkState['presence'][number];
type PresenceLogEntry = UnifiNetworkState['presenceLog'][number];

// ============================================================================
// Org-Aware Query Param
// ============================================================================

/**
 * Returns "?orgSlug=<slug>" when an org context is active, otherwise "".
 * Appended to all UniFi API calls so the backend scopes the request
 * to the correct org or personal namespace.
 */
async function getOwnerQueryParam(): Promise<string> {
  const orgSlug = await getValidatedOrganizationContextHeaderValue();
  return orgSlug ? `?orgSlug=${encodeURIComponent(orgSlug)}` : '';
}

// ============================================================================
// Store
// ============================================================================

export const useUnifiNetworkStore = create<UnifiNetworkState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      hasApiKey: false,
      hasWsCredentials: false,
      wsUsername: '',
      isConnected: false,
      host: '',
      siteId: undefined,
      siteName: undefined,
      sites: [],
      devices: [],
      clients: [],
      networks: [],
      trackedClients: [],
      excludedClients: [],
      presence: [],
      presenceLog: [],
      isLoading: false,
      isConnecting: false,
      error: null,

      loadSettings: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<UnifiNetworkSettings>(`${API_BASE}/settings${q}`);
          set((state) => {
            state.hasApiKey = response.data?.hasApiKey ?? false;
            state.hasWsCredentials = response.data?.hasWsCredentials ?? false;
            state.wsUsername = response.data?.wsUsername ?? '';
            state.isConnected = response.data?.isConnected ?? false;
            state.host = response.data?.host ?? '';
            state.siteId = response.data?.siteId;
            state.siteName = response.data?.siteName;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to load settings';
          });
        }
      },

      saveSettings: async (host, apiKey, wsUsername?, wsPassword?) => {
        set((state) => { state.isConnecting = true; state.error = null; });
        try {
          const q = await getOwnerQueryParam();
          const body: Record<string, string> = { host, apiKey };
          if (wsUsername) body.wsUsername = wsUsername;
          if (wsPassword) body.wsPassword = wsPassword;
          const response = await api.put<UnifiSettingsUpdateResponse>(`${API_BASE}/settings${q}`, body);
          const data = response.data;
          set((state) => {
            state.hasApiKey = true;
            state.isConnected = data?.isConnected ?? false;
            state.host = host;
            state.isConnecting = false;
          });
          // Reload data if Pi is connected
          if (data?.isConnected) {
            await Promise.all([get().loadDevices(), get().loadNetworks()]);
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to save settings';
            state.isConnecting = false;
          });
          throw error;
        }
      },

      saveWsCredentials: async (wsUsername, wsPassword) => {
        try {
          const q = await getOwnerQueryParam();
          await api.put(`${API_BASE}/settings/ws-credentials${q}`, { wsUsername, wsPassword });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to save WebSocket credentials';
          });
          throw error;
        }
      },

      removeSettings: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const q = await getOwnerQueryParam();
          await api.delete(`${API_BASE}/settings${q}`);
          set((state) => {
            state.hasApiKey = false;
            state.hasWsCredentials = false;
            state.wsUsername = '';
            state.isConnected = false;
            state.host = '';
            state.siteId = undefined;
            state.siteName = undefined;
            state.sites = [];
            state.devices = [];
            state.clients = [];
            state.networks = [];
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to remove settings';
            state.isLoading = false;
          });
        }
      },

      requestDiscovery: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const q = await getOwnerQueryParam();
          await api.post(`${API_BASE}/discover${q}`);
          set((state) => { state.isLoading = false; });
          // Reload data after discovery request
          setTimeout(() => {
            get().loadDevices();
            get().loadNetworks();
          }, 3000); // Give Pi time to respond
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to request discovery';
            state.isLoading = false;
          });
        }
      },

      loadDevices: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<UnifiNetworkDevice[]>(`${API_BASE}/devices${q}`);
          const data = response.data;
          set((state) => {
            state.devices = data ?? [];
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to load devices';
          });
        }
      },

      loadClients: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<UnifiNetworkClient[]>(`${API_BASE}/clients${q}`);
          const data = response.data;
          set((state) => {
            state.clients = data ?? [];
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to load clients';
          });
        }
      },

      loadNetworks: async () => {
        // Networks come from Pi discovery events
        // For now, they're not separately cached — they arrive with devicesDiscovered
      },

      restartDevice: async (deviceId) => {
        try {
          const q = await getOwnerQueryParam();
          await api.post(`${API_BASE}/devices/${encodeURIComponent(deviceId)}/restart${q}`);
          // Reload devices after action
          setTimeout(() => get().loadDevices(), 2000);
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to restart device';
          });
        }
      },

      powerCyclePort: async (deviceId, portIdx) => {
        try {
          const q = await getOwnerQueryParam();
          await api.post(`${API_BASE}/devices/${encodeURIComponent(deviceId)}/ports/${portIdx}/power-cycle${q}`);
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to power cycle port';
          });
        }
      },

      updateDeviceLocation: async (deviceId, locationName) => {
        try {
          const q = await getOwnerQueryParam();
          await api.put(`${API_BASE}/devices/${encodeURIComponent(deviceId)}${q}`, { locationName });
          set((state) => {
            const device = state.devices.find(d => d.id === deviceId);
            if (device) device.locationName = locationName;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to update device location';
          });
        }
      },

      trackClient: async (clientMac, userId, clientName) => {
        try {
          const q = await getOwnerQueryParam();
          await api.post(`${API_BASE}/clients/${encodeURIComponent(clientMac)}/track${q}`, { userId, clientName });
          await get().loadTrackedClients();
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to track client';
          });
        }
      },

      untrackClient: async (clientMac) => {
        try {
          const q = await getOwnerQueryParam();
          await api.delete(`${API_BASE}/clients/${encodeURIComponent(clientMac)}/track${q}`);
          await get().loadTrackedClients();
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to untrack client';
          });
        }
      },

      loadTrackedClients: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<TrackedClient[]>(`${API_BASE}/tracked-clients${q}`);
          set((state) => { state.trackedClients = response.data ?? []; });
        } catch { /* non-fatal */ }
      },

      excludeClient: async (clientMac, clientName) => {
        try {
          const q = await getOwnerQueryParam();
          await api.post(`${API_BASE}/clients/${encodeURIComponent(clientMac)}/exclude${q}`, { clientName });
          await get().loadExcludedClients();
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to exclude client';
          });
        }
      },

      includeClient: async (clientMac) => {
        try {
          const q = await getOwnerQueryParam();
          await api.delete(`${API_BASE}/clients/${encodeURIComponent(clientMac)}/exclude${q}`);
          await get().loadExcludedClients();
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to include client';
          });
        }
      },

      loadExcludedClients: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<ExcludedClient[]>(`${API_BASE}/excluded-clients${q}`);
          set((state) => { state.excludedClients = response.data ?? []; });
        } catch { /* non-fatal */ }
      },

      loadPresence: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<PresenceEntry[]>(`${API_BASE}/presence${q}`);
          set((state) => { state.presence = response.data ?? []; });
        } catch { /* non-fatal */ }
      },

      loadPresenceLog: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<PresenceLogEntry[]>(`${API_BASE}/presence/log${q}&limit=20`);
          set((state) => { state.presenceLog = response.data ?? []; });
        } catch { /* non-fatal */ }
      },

      clearError: () => {
        set((state) => { state.error = null; });
      },

      reset: () => {
        set((state) => {
          state.hasApiKey = false;
          state.isConnected = false;
          state.host = '';
          state.siteId = undefined;
          state.siteName = undefined;
          state.sites = [];
          state.devices = [];
          state.clients = [];
          state.networks = [];
          state.isLoading = false;
          state.isConnecting = false;
          state.error = null;
        });
      },
    }))
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const useUnifiNetworkDevices = () => useUnifiNetworkStore(s => s.devices);
export const useUnifiNetworkClients = () => useUnifiNetworkStore(s => s.clients);
export const useUnifiNetworkConnection = () =>
  useUnifiNetworkStore(s => ({
    hasApiKey: s.hasApiKey,
    isConnected: s.isConnected,
    isConnecting: s.isConnecting,
    host: s.host,
    siteName: s.siteName,
    error: s.error,
  }));
